import styled from '@emotion/styled';
import {
	Css,
	ITheme,
	InstagramIcon,
	Link,
	Responsive,
	XLogoIcon,
	YouTubeIcon,
} from '../../../ui';
import FacebookIcon from '../../../assets/icons/socials/facebook.svg';
import LinkedInIcon from '../../../assets/icons/socials/linkedin.svg';

export enum ISocialLinkItemPlatform {
	Facebook = 'Facebook',
	Instagram = 'Instagram',
	Twitter = 'Twitter',
	LinkedIn = 'LinkedIn',
	YouTube = 'YouTube',
	Pinterest = 'Pinterest',
	GooglePlayStore = 'Google Play Store',
	AppStore = 'Apple App Store',
}

export interface ISocialLinksWithLinks {
	links: ISocialLinkItem[];
	variant?: 'dark' | 'light';
	shareLinkUrl?: never;
}

export interface ISocialLinksWithShare {
	links?: undefined;
	variant?: 'dark' | 'light';
	shareLinkUrl: string;
}

export type ISocialLinksProps = ISocialLinksWithLinks | ISocialLinksWithShare;

export interface ISocialLinkItem {
	url: string;
	platform: ISocialLinkItemPlatform;
	shareLinkUrl?: string;
}

export function SocialLinks({
	links,
	variant = 'light',
	shareLinkUrl,
}: ISocialLinksProps) {
	const shareText = 'Check this out!';

	const shareToSocialMediaLinks = shareLinkUrl && {
		links: [
			{
				url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
					shareLinkUrl,
				)}&quote=${shareText}`,
				platform: ISocialLinkItemPlatform.Facebook,
				shareLinkUrl,
			},
			{
				url: `https://x.com/intent/tweet?url=${encodeURIComponent(
					shareLinkUrl,
				)}&text=${shareText}`,
				platform: ISocialLinkItemPlatform.Twitter,
				shareLinkUrl,
			},
			{
				url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
					shareLinkUrl,
				)}&text=${shareText}`,
				platform: ISocialLinkItemPlatform.LinkedIn,
				shareLinkUrl,
			},
		],
	};

	const linksToMap =
		shareLinkUrl && shareToSocialMediaLinks
			? shareToSocialMediaLinks.links
			: links;

	return (
		<SocialLinksWrap variant={variant}>
			{linksToMap?.map(renderLink)}
		</SocialLinksWrap>
	);
}

function renderLink({ platform, url, shareLinkUrl }: ISocialLinkItem) {
	const Icon = getIconForPlatform(platform);
	const accessibleName = shareLinkUrl
		? `Share to ${platform}`
		: `Open ${platform} in new window`;

	return (
		<Link
			key={url}
			href={url}
			aria-label={accessibleName}
			target="_blank"
			rel="noopener noreferrer"
			hideExternalIndicator
		>
			<Icon aria-hidden="true" />
		</Link>
	);
}

function getIconForPlatform(platform: ISocialLinkItemPlatform) {
	switch (platform) {
		case 'Facebook':
			return FacebookIcon;

		case 'Instagram':
			return InstagramIcon;

		case 'Twitter':
			return XLogoIcon;

		case 'LinkedIn':
			return LinkedInIcon;

		case 'YouTube':
			return YouTubeIcon;

		default:
			throw new Error(`No icon available for platform: ${platform}`);
	}
}

const SocialLinksWrap = styled.div<
	{ variant?: ISocialLinksProps['variant'] },
	ITheme
>`
	padding-top: ${Css.pxToRem(32)};
	display: flex;
	justify-content: flex-start;
	gap: ${Css.pxToRem(16)};
	width: 100%;

	svg {
		width: 24px;
		height: unset;

		path {
			fill: ${({ variant, theme }) =>
				variant === 'light' ? theme.color.onSecondary : theme.color.secondary};
		}

		&:hover {
			path {
				fill: ${({ variant, theme }) =>
					variant === 'light'
						? theme.color.primaryLight
						: theme.button?.textButton.hover.font.color};
			}
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: ${Css.pxToRem(24)};
		padding-top: 0;
	}
`;
